/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Type } from "@angular/core";
import { Params, Route } from "@angular/router";
import { TenantId, uuid } from "@src/app/model/abstract-object";
import { ChartParms, TableView } from "@src/app/model/sys/nav-item";
import { User } from "@src/app/model/user";
import { CanDeactivateGuard } from "@src/app/shared/guards/can-deactivate.guard";
import { LoggedInGuard } from "@src/app/shared/guards/loggedin.guard";
import { TenantService } from "@src/app/shared/tenant.service";
import { MyInjector } from "@src/app/app.module";

export class NavRoute {
    private _url: string;
    private _baseUrl: string;
    private newComponentFn: () => Type<unknown>;

    private viewMethod: (u: User) => TableView[];
    private _userViews: TableView[] = [];
    private _userViewsTeamId: TenantId = null;

    private _icon: string;

    public _charts: ChartParms[];

    constructor(url: string, private componentFn: () => Type<unknown>, 
        icon: string, private itemComponentFn: () => Type<unknown> = null) {
        this._url = ':tenantId/' + url;
        this._baseUrl = url;
        this._icon = icon;
        if (!itemComponentFn) {
            this.itemComponentFn = componentFn;
        }
        this.newComponentFn = componentFn;

        if (NavRoute.index.has(url)) {
            console.error('Already have path ', {url, paths: NavRoute.index});
        }
        NavRoute.index.set(url, this);
    }
    public get baseUrl() {
        return this._baseUrl;
    }

    public get icon() {
        return this._icon;
    }

    public setIcon(icon: string) {
        this._icon = icon;
        return this;
    }

    public configureUserViews(user: User) {
        const teamId = user?.currentTeam?.id ?? null;
        if (this._userViews.length === 0 || this._userViewsTeamId !== teamId) {
            this._userViews = this.getViews(user);
            this._userViewsTeamId = teamId;
        }
    }

    public get userViews() {
        return this._userViews;
    }

    public get url() {
        return this._url;
    }

    public get itemUrl() {
        return this._url + '/:itemId';
    }

    public getForceListRouteName(tenantId: TenantId) {
        return `/${tenantId}/${this.baseUrl}`;
    }

    public getListRouteName() {
        const tenantSvc = MyInjector.instance.get(TenantService);
        return `/${tenantSvc.getCurrentTenantId()}/${this.baseUrl}`;
    }

    public getListRouterLink(parms: Params = {}) {
        return [this.getListRouteName(), parms];
    }

    public getIdUrl(id: uuid, forceTeam?: TenantId) {
        const tenantSvc = MyInjector.instance.get(TenantService);
        const tenantId = forceTeam || tenantSvc.getCurrentTenantId();
        return `/${tenantId}/${this.baseUrl}/${id}`
    }

    public get newUrl() {
        return this.getListRouteName() + '/NEW';
    }

    public get openRoute() {
        return {
            path: this._baseUrl,
            component: this.componentFn(),
            pathMatch: 'full',
            data: { animation: 'openRoute' }
        } as Route;
    }
    public get openItemRoute() {
        return {
            path: this.itemUrl,
            component: this.itemComponentFn(),
            pathMatch: 'full',
            data: { animation: 'itemRoute' }
        } as Route;
    }

    public setNewComponent(c: () => Type<unknown>) {
        this.newComponentFn = c;
        return this;
    }

    public setItemComponent(c: () => Type<unknown>) {
        this.itemComponentFn = c;
        return this;
    }

    public get listRoute() {
        return {
            path: this.url,
            component: this.componentFn(),
            canDeactivate: [CanDeactivateGuard],
            canActivate: [LoggedInGuard],
            pathMatch: 'full',
            data: { animation: 'listRoute' }
        } as Route;
    }

    public get itemRoute() {
        return {
            path: this.itemUrl,
            component: this.itemComponentFn(),
            canDeactivate: [CanDeactivateGuard],
            canActivate: [LoggedInGuard],
            pathMatch: 'full',
            data: { animation: 'itemRoute' }
        } as Route;
    }

    public get newRoute() {
        return {
            path: this.url + '/NEW',
            component: this.newComponentFn(),
            canDeactivate: [CanDeactivateGuard],
            canActivate: [LoggedInGuard],
            pathMatch: 'full',
            data: { animation: 'newRoute' }
        } as Route;
    }


    setViews( method: (u: User) => TableView[]) {
        this.viewMethod = method;
        return this;
    }

    setCharts(cp: ChartParms[]) {
        this._charts = cp;
        return this;
    }

    getCharts() {
        return this._charts;
    }

    getViews(u: User) {
        return this.viewMethod ? this.viewMethod(u) : [];
    }

    private static index = new Map<string, NavRoute>();

    public static getRoute(path) : NavRoute {
        if (!NavRoute.index.has(path)) {
            if (path !== 'demos') {
                console.error('Do not have path ', {path, paths: NavRoute.index});
            }
        }
        return NavRoute.index.get(path);
    }
}
